<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-head">
        <div class="auth-title">登录</div>
        <div class="auth-subtitle">爱分享欢迎您的加入</div>
      </div>

      <form class="auth-form" @submit.prevent="submit">
        <label class="field">
          <span class="field-label">邮箱</span>
          <input v-model="form.email" class="field-input" type="email" placeholder="邮箱" autocomplete="email" />
        </label>

        <label class="field">
          <span class="field-label">密码</span>
          <input v-model="form.password" class="field-input" type="password" placeholder="密码（至少6位）"
            autocomplete="current-password" />
        </label>
        <div style="text-align: center;">
          <button class="pink-btn" type="submit" :disabled="loading">
            {{ loading ? "登录中..." : "登录" }}
          </button>
        </div>
        <div class="extra-tip">
          没有账号？ <a href="/users/register">去注册</a>&nbsp;&nbsp;&nbsp;&nbsp;
          <a href="/users/password_retrieve">找回密码</a>
        </div>
      </form>
    </div>

    <AppFooter active="me" />
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import { message } from "ant-design-vue";
import AppFooter from "~/components/AppFooter.vue";

const criticalCss = `
  .auth-page {
    min-height: calc(100vh - 64px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ffeaf1 0%, #fff5f9 100%);
    padding: 24px 16px;
  }

  .auth-card {
    width: 100%;
    max-width: 420px;
    border-radius: 12px;
    box-shadow: 0 12px 30px rgba(251, 114, 153, 0.16);
    border: 1px solid #ffe0eb;
    background: #fff;
  }

  .auth-head {
    text-align: center;
    margin-bottom: 12px;
  }

  .auth-title {
    font-size: 24px;
    font-weight: 700;
    color: #fb7299;
  }

  .auth-subtitle {
    margin-top: 4px;
    font-size: 14px;
    color: #a06b80;
  }

  .auth-form {
    margin-top: 12px;
  }

  .field {
    display: grid;
    gap: 6px;
    margin: 12px;
  }

  .field-label {
    font-size: 12px;
    color: #8c9196;
  }

  .field-input {
    width: 100%;
    border-radius: 8px;
    padding: 10px 12px;
    border: 1px solid #f3d7e1;
    background: #fff;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
  }

  .field-input:focus {
    border-color: #fb7299;
    box-shadow: 0 0 0 2px rgba(251, 114, 153, 0.2);
  }

  .pink-btn {
    --pink: #fb7299;
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 10px 12px;
    background: var(--pink);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(251, 114, 153, 0.35);
  }

  .pink-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .extra-tip {
    margin-top: 10px;
    font-size: 13px;
    color: #8c9196;
    text-align: center;
  }
`;

useHead({
  style: [{ children: criticalCss }],
});

const loading = ref(false);

const form = reactive({
  email: "",
  password: "",
});

const validateForm = () => {
  if (!form.email.trim()) {
    message.error("请输入邮箱");
    return false;
  }
  if (!form.password.trim()) {
    message.error("请输入密码");
    return false;
  }
  if (form.password.trim().length < 6) {
    message.error("密码至少6位");
    return false;
  }
  return true;
};

function submit() {
  if (!validateForm()) return;
  onFinish();
}

async function onFinish() {
  loading.value = true;
  try {
    const { data } = await api_UserLogin({
      method: "POST",
      body: {
        email: form.email,
        password: form.password,
      },
      server: false,
      key: "api_UserLogin" + form.email + new Date().getMilliseconds(),
    });

    const curData = data.value as IApiResponse<any>;

    if (curData?.code === 1) {
      const accessTokenCookie = useCookie("token", {
        maxAge: 60 * 60 * 24 * 365,
      });
      accessTokenCookie.value = curData.data;
      if (import.meta.client) {
        const link = document.createElement("a");
        link.href = "/";
        link.rel = "noopener";
        link.click();
      } else {
        await navigateTo("/", { replace: true });
      }
    } else {
      message.error(curData?.message || "登录失败");
    }
  } catch (error: any) {
    console.error("Login failed:", error);
    message.error(error.message || "网络错误，请稍后重试");
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffeaf1 0%, #fff5f9 100%);
  padding: 24px 16px;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(251, 114, 153, 0.16);
  border: 1px solid #ffe0eb;
  background: #fff;
}

.auth-head {
  text-align: center;
  margin-bottom: 12px;
}

.auth-title {
  font-size: 24px;
  font-weight: 700;
  color: #fb7299;
}

.auth-subtitle {
  margin-top: 4px;
  font-size: 14px;
  color: #a06b80;
}

.auth-form {
  margin-top: 12px;
}

.field {
  display: grid;
  gap: 6px;
  margin: 12px;
}

.field-label {
  font-size: 12px;
  color: #8c9196;
}

.field-input {
  width: 100%;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid #f3d7e1;
  background: #fff;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.field-input:focus {
  border-color: #fb7299;
  box-shadow: 0 0 0 2px rgba(251, 114, 153, 0.2);
}

.pink-btn {
  --pink: #fb7299;
  width: 90%;
  border: none;
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--pink);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(251, 114, 153, 0.35);
}

.pink-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.extra-tip {
  margin-top: 10px;
  font-size: 13px;
  color: #8c9196;
  text-align: center;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 4px 0;
    box-shadow: none;
    background: transparent;
  }
}
</style>
